import { useRegion } from '@/context';

/**
 * What the SPA knows about the caller's email address.
 *
 * Four answers, because the two that are not answers behave differently from
 * both of the ones that are:
 *
 * - `pending` — no verdict yet, the claims are still being read. Show a
 *   placeholder; decide nothing.
 * - `verified` — the token asserts a verified address.
 * - `unverified` — the token asserts an unverified address. The only state
 *   that may send someone to the verify-email screen.
 * - `unprovable` — the question cannot be answered from this session: the
 *   token exchange failed, or the token carries no verified-email claim at
 *   all. Never evidence of anything about the address.
 */
export type TEmailVerificationState = 'pending' | 'verified' | 'unverified' | 'unprovable';

/**
 * The one place the verified-email question is answered (ESCLOUD-286).
 *
 * It exists because three call sites — the router guard, the tenant redirect
 * and the onboarding wizard — used to each combine `claimsLoaded`,
 * `claimsFailed` and the claim themselves, and one of them left `claimsFailed`
 * out: a user whose token exchange failed has `claimsLoaded === true` and no
 * claim, which reads as "unverified" and parked them on a screen whose only
 * way forward is the very token refresh that is failing. Two remain — the
 * tenant redirect stopped asking altogether, because the guard is mounted above
 * every route that renders it and had already decided (ESCLOUD-282).
 *
 * Every consumer must handle `unprovable` on purpose, and none of them may
 * treat it as `unverified`: an unproven absence must never be acted on, the
 * same rule the tenant registry established (ESCLOUD-268). What that means
 * differs by consumer — a guard falls through to the route rather than
 * redirecting, the wizard says so and offers a way out — but never a redirect
 * or a wait on evidence nobody has.
 *
 * A missing claim lands here too, and that is not hypothetical: the claim is
 * minted by an Auth0 post-login action, so an environment where the API image
 * is deployed ahead of `tools/deploy-auth0.sh` serves tokens without it. Read
 * as `unverified`, that redirects every already-verified user into the funnel
 * to wait for a claim that will never arrive.
 */
export function useEmailVerificationState(): TEmailVerificationState {
    const { emailVerifiedClaim, claimsLoaded, claimsFailed } = useRegion();

    // Ordered: a refresh in flight drops claimsLoaded, and a failure sets both
    // claimsLoaded and claimsFailed — so "not loaded" is asked first, and the
    // claim is only consulted once it is known to have been established.
    if (!claimsLoaded) {
        return 'pending';
    }

    if (claimsFailed || emailVerifiedClaim === undefined) {
        return 'unprovable';
    }

    return emailVerifiedClaim ? 'verified' : 'unverified';
}
